/**
 * Undo Snackbar - Shows undoable action notifications with countdown timer.
 *
 * Features:
 * - Appears in bottom-left corner
 * - 5-second countdown with visual progress bar
 * - Undo button to revert the action
 * - Only one snackbar at a time (new actions replace existing)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { Z } from '@shared/constants/zIndex';
import { usePortalContainer } from '@shared/hooks';

import {
  UndoSnackbarContext,
  type UndoState,
  type UndoSnackbarContextValue,
} from './UndoSnackbarContext';

/** Duration in milliseconds before auto-dismiss */
const UNDO_DURATION_MS = 5000;

/** Update interval for countdown animation */
const COUNTDOWN_INTERVAL_MS = 50;

interface UndoSnackbarNotificationProps {
  state: UndoState;
  remainingMs: number;
  onUndo: () => void;
  onDismiss: () => void;
}

/**
 * Visual notification component for the undo snackbar.
 */
function UndoSnackbarNotification({
  state,
  remainingMs,
  onUndo,
  onDismiss,
}: UndoSnackbarNotificationProps): React.ReactElement {
  const progress = remainingMs / UNDO_DURATION_MS;

  return (
    <div
      className="pointer-events-auto fixed bottom-4 left-4"
      style={{ zIndex: Z.TOAST }}
      role="alert"
      aria-live="polite"
    >
      {/* The theme's popover surface, so it follows the active theme and its
          light/dark mode. An inverted surface (foreground as background) read
          as a white slab in every dark theme, since they share a near-white
          foreground. */}
      <div
        data-testid="undo-snackbar-surface"
        className="bg-popover text-popover-foreground border-border flex min-w-[280px] items-center gap-3 rounded-xl border px-4 py-3 shadow-lg"
      >
        {/* Message */}
        <span className="flex-1 text-sm font-medium">{state.message}</span>

        {/* Progress bar */}
        <div className="bg-muted h-1.5 w-12 overflow-hidden rounded-full">
          <div
            className="bg-brand h-full transition-all ease-linear"
            style={{
              width: `${progress * 100}%`,
              transitionDuration: `${COUNTDOWN_INTERVAL_MS}ms`,
            }}
          />
        </div>

        {/* Undo button */}
        <button
          type="button"
          onClick={onUndo}
          className="text-brand hover:text-brand/80 text-sm font-semibold transition-colors"
        >
          Undo
        </button>

        {/* Dismiss button */}
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted-foreground hover:text-popover-foreground transition-colors"
          aria-label="Dismiss"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

interface UndoSnackbarProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that manages undo snackbar state and renders the notification.
 */
export function UndoSnackbarProvider({ children }: UndoSnackbarProviderProps): React.ReactElement {
  const [currentUndo, setCurrentUndo] = useState<UndoState | null>(null);
  const [remainingMs, setRemainingMs] = useState(UNDO_DURATION_MS);

  // Refs for timer management
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const undoIdRef = useRef(0);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Dismiss the snackbar
  const dismiss = useCallback(() => {
    clearTimers();
    setCurrentUndo(null);
    setRemainingMs(UNDO_DURATION_MS);
  }, [clearTimers]);

  // Show a new undo snackbar
  const showUndo = useCallback(
    (message: string, undoCallback: () => void | Promise<void>) => {
      // Clear any existing timers
      clearTimers();

      // Create new undo state
      const newId = ++undoIdRef.current;
      const expiresAt = Date.now() + UNDO_DURATION_MS;

      setCurrentUndo({
        id: newId,
        message,
        undoCallback,
        expiresAt,
      });
      setRemainingMs(UNDO_DURATION_MS);

      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        setRemainingMs(prev => {
          const newRemaining = Math.max(0, prev - COUNTDOWN_INTERVAL_MS);
          return newRemaining;
        });
      }, COUNTDOWN_INTERVAL_MS);

      // Set auto-dismiss timer
      dismissTimerRef.current = setTimeout(() => {
        dismiss();
      }, UNDO_DURATION_MS);
    },
    [clearTimers, dismiss]
  );

  // Handle undo button click
  const handleUndo = useCallback(async () => {
    if (!currentUndo) return;

    const { undoCallback } = currentUndo;

    // Dismiss first to prevent double-clicks
    dismiss();

    // Execute the undo callback
    try {
      await undoCallback();
    } catch (error) {
      console.error('Undo action failed:', error);
      // Error toast would be shown by the callback itself if needed
    }
  }, [currentUndo, dismiss]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  // Memoize the context value so its identity stays stable across countdown
  // ticks. The countdown interval re-renders this provider every 50ms; without
  // memoization a fresh value object would re-render every useUndoSnackbar
  // consumer (the email/inbox list pages) 20x/sec, freezing the UI on iOS.
  const contextValue = useMemo<UndoSnackbarContextValue>(
    () => ({ showUndo, dismiss }),
    [showUndo, dismiss]
  );

  // Follows the fullscreen element so an undo offer is not lost behind one.
  const portalContainer = usePortalContainer();

  return (
    <UndoSnackbarContext.Provider value={contextValue}>
      {children}
      {currentUndo &&
        createPortal(
          <UndoSnackbarNotification
            state={currentUndo}
            remainingMs={remainingMs}
            onUndo={handleUndo}
            onDismiss={dismiss}
          />,
          portalContainer
        )}
    </UndoSnackbarContext.Provider>
  );
}
